import * as React from 'react';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { Button, Card, Empty, Modal } from 'antd';
import { ApplicationDefinitionConditionalEditStore } from '../../stores';
import { AdditionalClassificationDto, AdditionalClassificationFieldChangeDto } from '../../types';
import './RefreshAdditionalClassificationModal.less';

interface Props {
    store: ApplicationDefinitionConditionalEditStore;
}

type DependencyChain = {
    key: string;
    label: string;
};

type MetadataChange = {
    key: string;
    displayName: string;
    classificationSlot: string;
    changes: {
        label: string;
        current: string;
        refreshed: string;
    }[];
};

const RefreshAdditionalClassificationModal: React.FC<Props> = ({ store }) => {
    const preview = store.additionalClassificationRefreshPreview;

    const flattenClassifications = (classification: AdditionalClassificationDto): AdditionalClassificationDto[] => [
        classification,
        ...(classification.children ?? []).reduce<AdditionalClassificationDto[]>(
            (children, child) => [...children, ...flattenClassifications(child)],
            []
        )
    ];

    const getMetadataChanges = (
        current: AdditionalClassificationDto,
        refreshed: AdditionalClassificationDto
    ): MetadataChange[] => {
        const refreshedBySlot = new Map(
            flattenClassifications(refreshed).map(classification => [classification.classificationSlot, classification])
        );

        return flattenClassifications(current)
            .map(currentClassification => {
                const refreshedClassification = refreshedBySlot.get(currentClassification.classificationSlot);

                if (!refreshedClassification) {
                    return undefined;
                }

                const changes = [
                    {
                        label: 'Display name',
                        current: currentClassification.displayName,
                        refreshed: refreshedClassification.displayName
                    },
                    {
                        label: 'System name',
                        current: currentClassification.systemName,
                        refreshed: refreshedClassification.systemName
                    }
                ].filter(change => change.current !== change.refreshed);

                if (!changes.length) {
                    return undefined;
                }

                return {
                    key: `${currentClassification.typeCode}-${currentClassification.classificationSlot}`,
                    displayName: currentClassification.displayName,
                    classificationSlot: currentClassification.classificationSlot,
                    changes
                };
            })
            .filter((change): change is MetadataChange => Boolean(change));
    };

    const getLeafDependencyPaths = (
        classification: AdditionalClassificationDto,
        labelPath: string[] = [],
        keyPath: string[] = []
    ): DependencyChain[] => {
        const nextLabelPath = [...labelPath, classification.displayName];
        const nextKeyPath = [...keyPath, `${classification.typeCode}:${classification.classificationSlot}`];

        if (!classification.children?.length) {
            return [
                {
                    key: nextKeyPath.join('>'),
                    label: nextLabelPath.join(' -> ')
                }
            ];
        }

        return classification.children.reduce<DependencyChain[]>(
            (paths, child) => [...paths, ...getLeafDependencyPaths(child, nextLabelPath, nextKeyPath)],
            []
        );
    };

    const renderChains = (chains: DependencyChain[]) => (
        <div className="refresh-additional-classification-modal__chains">
            {chains.map(chain => (
                <div key={chain.key} className="refresh-additional-classification-modal__chain">
                    {chain.label}
                </div>
            ))}
        </div>
    );

    const renderChainCard = (title: string, chains: DependencyChain[]) => (
        <Card
            size="small"
            bordered
            className="refresh-additional-classification-modal__structure-card"
            title={<span className="refresh-additional-classification-modal__structure-title">{title}</span>}
        >
            {renderChains(chains)}
        </Card>
    );

    const renderChangeRow = (type: 'added' | 'removed', field: AdditionalClassificationFieldChangeDto) => {
        const isAdded = type === 'added';

        return (
            <div
                key={`${type}-${field.typeCode}-${field.classificationSlot}`}
                className={`refresh-additional-classification-modal__change-row refresh-additional-classification-modal__change-row--${type}`}
            >
                <div className="refresh-additional-classification-modal__change-content">
                    {isAdded ? (
                        <PlusCircleOutlined className="refresh-additional-classification-modal__change-icon" />
                    ) : (
                        <MinusCircleOutlined className="refresh-additional-classification-modal__change-icon" />
                    )}
                    <div className="refresh-additional-classification-modal__change-copy">
                        <div className="refresh-additional-classification-modal__change-title">
                            {field.displayName} will be {isAdded ? 'added' : 'removed'}
                        </div>
                        <div className="refresh-additional-classification-modal__change-subtitle">
                            {field.systemName} | {field.classificationSlot}
                        </div>
                    </div>
                </div>
                <span className="refresh-additional-classification-modal__change-count">1 field</span>
            </div>
        );
    };

    const renderChanges = (
        addedFields: AdditionalClassificationFieldChangeDto[],
        removedFields: AdditionalClassificationFieldChangeDto[]
    ) => {
        const hasChanges = addedFields.length > 0 || removedFields.length > 0;

        return (
            <div className="refresh-additional-classification-modal__changes">
                <div className="refresh-additional-classification-modal__section-title">Changes</div>
                {hasChanges ? (
                    <div className="refresh-additional-classification-modal__change-list">
                        {addedFields.map(field => renderChangeRow('added', field))}
                        {removedFields.map(field => renderChangeRow('removed', field))}
                    </div>
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No fields will be added or removed" />
                )}
            </div>
        );
    };

    const renderMetadataChanges = (current: AdditionalClassificationDto, refreshed: AdditionalClassificationDto) => {
        const metadataChanges = getMetadataChanges(current, refreshed);

        if (!metadataChanges.length) {
            return null;
        }

        return (
            <div className="refresh-additional-classification-modal__metadata">
                <div className="refresh-additional-classification-modal__section-title">Metadata changes</div>
                <div className="refresh-additional-classification-modal__metadata-list">
                    {metadataChanges.map(metadataChange => (
                        <div key={metadataChange.key} className="refresh-additional-classification-modal__metadata-row">
                            <div className="refresh-additional-classification-modal__metadata-title">
                                {metadataChange.displayName} ({metadataChange.classificationSlot})
                            </div>
                            {metadataChange.changes.map(change => (
                                <div
                                    key={change.label}
                                    className="refresh-additional-classification-modal__metadata-change"
                                >
                                    <span>{change.label}</span>
                                    <span>Current: {change.current}</span>
                                    <span>SICS: {change.refreshed}</span>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderPreview = () => {
        if (!preview) {
            return <Empty description="No refresh preview available" />;
        }

        return (
            <>
                <div
                    data-id="additional-classification-refresh-structure-cards"
                    className="refresh-additional-classification-modal__structure-cards"
                >
                    {renderChainCard('Current structure', getLeafDependencyPaths(preview.current))}
                    {renderChainCard('SICS structure', getLeafDependencyPaths(preview.refreshed))}
                </div>
                {renderChanges(preview.addedFields, preview.removedFields)}
                {renderMetadataChanges(preview.current, preview.refreshed)}
            </>
        );
    };

    return (
        <Modal
            title="Refresh from SICS"
            className="alpha-portal-dialog refresh-additional-classification-modal"
            visible={store.additionalClassificationRefreshModalVisible}
            onCancel={store.closeAdditionalClassificationRefreshModal}
            maskClosable={false}
            destroyOnClose
            width={760}
            centered
            footer={[
                <Button
                    data-id="refresh-additional-classification-cancel"
                    className="light"
                    key="cancel"
                    size="large"
                    onClick={store.closeAdditionalClassificationRefreshModal}
                >
                    Cancel
                </Button>,
                <Button
                    data-id="refresh-additional-classification-confirm"
                    key="confirm"
                    size="large"
                    type="primary"
                    onClick={store.confirmAdditionalClassificationRefresh}
                    disabled={!preview}
                >
                    Update Classification
                </Button>
            ]}
        >
            {renderPreview()}
        </Modal>
    );
};

export default observer(RefreshAdditionalClassificationModal);
